import React, { useRef, useState } from 'react'
import Menu, { WMOption } from '@/component/menu/Menu.tsx'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, Link } from 'react-router-dom'
import CarearlyLogo from '@/assets/ic_carearly_logo.svg?react'
import SolidButton from '@/component/button/SolidButton.tsx'
import Hamburger from '@/assets/ic_hambuger.svg?react'
import { useTranslation } from 'react-i18next'
import LanguageIcon from '@/assets/img_language.svg?react'
import AccountMenu from '@/component/layout/TopNav/AccountMenu.tsx'
import Breadcrumb from '@/component/layout/TopNav/Breadcrumb.tsx'
import SideNavDrawer, { SIDE_NAV_DRAWER_ID } from '@/component/layout/SideNav/SideNavDrawer.tsx'
import { useSideNavStore } from '@/store/SideNavStore.ts'
import { useLanguageOptions } from '@/locales/useLanguageOptions.ts'

type IProps = {
    className?: string
    type?: TYPE_TopNav
}

export enum TYPE_TopNav {
    DEFAULT,
    GUIDE
}

const TopNav: React.FunctionComponent<IProps> = ({ className, type = TYPE_TopNav.DEFAULT }) => {
    const { isLoggedIn } = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [showMobileMenu, setShowMobileMenu] = useState(false)
    const isDrawerOpen = useSideNavStore((state) => state.isDrawerOpen)
    const toggleDrawer = useSideNavStore((state) => state.toggleDrawer)
    const menuButtonRef = useRef<HTMLButtonElement>(null)
    const { languages, changeLanguage } = useLanguageOptions()

    if (type == TYPE_TopNav.DEFAULT) {
        return (
            <>
                <header className={'top-nav top-nav--workspace relative'}>
                    <div className={`${className} top-nav__bar`}>
                        <button
                            ref={menuButtonRef}
                            type={'button'}
                            className={'top-nav__menu-button'}
                            onClick={toggleDrawer}
                            aria-label={isDrawerOpen ? 'Close navigation menu' : 'Open navigation menu'}
                            aria-expanded={isDrawerOpen}
                            aria-controls={isDrawerOpen ? SIDE_NAV_DRAWER_ID : undefined}
                        >
                            <Hamburger />
                        </button>
                        <Link to={'/'} className={'top-nav__brand flex-none'} aria-label={t('app.title.carearly')}>
                            <CarearlyLogo className={'top-nav__logo'} />
                        </Link>
                        <span className={'top-nav__context-divider'} />
                        <Breadcrumb />
                        <div className={'top-nav__actions'}>
                            <AccountMenu />
                        </div>
                    </div>
                </header>
                <SideNavDrawer returnFocusRef={menuButtonRef} />
            </>
        )
    }

    const languageItems: WMOption[] = languages.map((language) => ({
        id: language.id,
        text: language.label,
        onClick: () => changeLanguage(language.id)
    }))

    const handleServiceClick = () => {
        setShowMobileMenu(false)
        if (isLoggedIn) {
            navigate('/dashboard')
        } else {
            navigate('/login')
        }
    }

    return (
        <>
            <header className={'top-nav top-nav--guide relative'}>
                <div
                    className={`${className} top-nav__bar w-full min-h-[80px] md:min-h-[60px] bg-white flex items-center px-[34px] md:px-[15px] border-b border-solid border-gray2 gap-[28px] md:gap-[12px]`}
                >
                    <div className={'hidden md:block flex-none'}>
                        <button
                            className={'top-nav__icon-button flex min-h-[40px] min-w-[40px] items-center justify-center'}
                            onClick={() => setShowMobileMenu(!showMobileMenu)}
                            aria-label={showMobileMenu ? 'Close navigation menu' : 'Open navigation menu'}
                            aria-expanded={showMobileMenu}
                        >
                            <Hamburger />
                        </button>
                    </div>
                    <Link to={'/'} className={'top-nav__brand flex-none'} aria-label={t('app.title.carearly')}>
                        <CarearlyLogo className={'w-[151px] md:w-[115px] md:h-[28px]'} />
                    </Link>
                    <div className={'top-nav__context min-w-0 grow flex items-center gap-[10px]'}>
                        <button
                            className={'top-nav__guide-link text-body md:hidden'}
                            onClick={() => navigate('/guide')}
                        >
                            {t('topNav.manual')}
                        </button>
                    </div>
                    <div className={'top-nav__actions flex flex-none items-center gap-[10px] sm:gap-[6px]'}>
                        <Menu
                            className={'top-nav__language-menu'}
                            items={languageItems}
                            ariaLabel={t('topNav.languageSelect')}
                            render={
                                <div
                                    className={'top-nav__icon-button flex min-h-[40px] min-w-[40px] select-none items-center justify-center'}
                                    aria-label={t('topNav.languageSelect')}
                                >
                                    <LanguageIcon className={'h-[18px] w-[18px] flex-none'} />
                                </div>
                            }
                        ></Menu>
                        <SolidButton
                            text={t('topNav.guide.service')}
                            className={'top-nav__service-button rounded-[12px] min-h-[43px] md:hidden min-w-[160px]'}
                            size={'small'}
                            onClick={handleServiceClick}
                        />
                    </div>
                </div>
            </header>
            {showMobileMenu && (
                <div className={'top-nav__mobile-layer hidden md:block fixed left-0 right-0 bottom-0 top-[60px] z-modal'}>
                    <button
                        aria-label="Close navigation menu"
                        className={'top-nav__mobile-backdrop absolute inset-0 bg-black/30'}
                        onClick={() => setShowMobileMenu(false)}
                    />
                    <nav className={'top-nav__mobile-drawer absolute left-0 top-0 grid h-full w-[280px] max-w-[86vw] content-start bg-white shadow-xl'}>
                        <button
                            className={'min-h-[52px] border-b border-solid border-gray2 px-[20px] text-left font-medium'}
                            onClick={handleServiceClick}
                        >
                            {t('topNav.guide.service')}
                        </button>
                        <button
                            className={'min-h-[52px] border-b border-solid border-gray2 px-[20px] text-left font-medium'}
                            onClick={() => {
                                setShowMobileMenu(false)
                                navigate('/guide')
                            }}
                        >
                            {t('topNav.guide.manual')}
                        </button>
                    </nav>
                </div>
            )}
        </>
    )
}

export default TopNav
